<template>
  <template v-if="callFeedback">
    <p>{{ callFeedback }}</p>
    <div class="content">
      <call-feedback-view></call-feedback-view>
      <loader-global></loader-global>
    </div>
  </template>

  <access-denied-screen
    v-else-if="accessView === 'denied-expired'"
    reason="expired"
  ></access-denied-screen>

  <access-denied-screen
    v-else-if="accessView === 'denied-forbidden'"
    reason="forbidden"
  ></access-denied-screen>

  <access-auth-screen
    v-else-if="accessView === 'auth'"
    :order-id="pendingOrderId"
    :bypass-key="pendingBypassKey"
    :blocked="accessBlocked"
    :blocked-remaining-seconds="accessBlockedSeconds"
    :attempts-remaining="accessAttemptsRemaining"
    @verified="onAccessVerified"
  ></access-auth-screen>

  <template v-else-if="orderFound">
    <div class="content" :class="orderData.client?.customize?.tracking_style_theme" style="padding-bottom: 88px">
      <app-header :hide-label="!!signatureLink"></app-header>
      <router-view></router-view>
      <div v-if="isClinicTheme" class="clinic-rx-footer">
        <img src="@/assets/img/company-logo.svg" alt="Rx2Go" width="66" height="17">
      </div>
      <language-btn></language-btn>
      <footer-btn></footer-btn>
      <!--      <app-download-btn></app-download-btn>-->
      <loader-global></loader-global>
    </div>
  </template>

  <error-page
    v-else-if="error"
    :status="error.status"
    :message="error.message"
  ></error-page>

  <div v-else class="content" style="display: flex; align-items: center; justify-content: center; min-height: 100vh;">
    <loader-global></loader-global>
  </div>
</template>

<script setup>
import { RouterView, useRoute } from 'vue-router'
import { onMounted, ref, nextTick, computed } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import CryptoJS from 'crypto-js'

import { loadScript } from 'vue-plugin-load-script'

import { getOrderInfo, orderUpdated } from '@/services/orderService'
import { getAccessStatus } from '@/services/accessService'
import FooterBtn from '@/components/FooterBtn/index.vue'
import LanguageBtn from '@/components/LanguageBtn'
import AppHeader from '@/components/AppHeader/index.vue'
import AppDownloadBtn from '@/components/AppDownloadBtn/index.vue'
import { dashUrl, trackingAuthEnabled } from '@/config'
import setDeliveringState from '@/utils/setDeliveringState'
import LoaderGlobal from '@/components/_common/loader-global.vue'
import { getRouteInfo } from '@/services/routeService'
import CallFeedbackView from '@/components/CallFeedbackView/index.vue'
import ErrorPage from '@/components/ErrorPage/index.vue'
import { getCall } from '@/services/callService'
import { initPushPrompt, confirmViewedIfOpenedFromPush } from '@/push/push-track-integration'
import AccessAuthScreen from '@/components/AccessAuth/AccessAuthScreen.vue'
import AccessDeniedScreen from '@/components/AccessAuth/AccessDeniedScreen.vue'
import {
  getStoredSession,
  resolveBypassKey,
  saveSession,
  setCurrentAccessToken,
  setCurrentBypassKey,
} from '@/utils/trackAccessAuth'
import NotationHelper from './utils/NotationHelper'

const orderFound = ref(false)
const orderData = ref(null)
const error = ref(null)
const callFeedback = ref(null)
const signatureLink = ref(null)
const accessView = ref(null)
const pendingOrderId = ref(null)
const pendingTimestamp = ref(null)
const pendingBypassKey = ref(null)
const accessBlocked = ref(false)
const accessBlockedSeconds = ref(0)
const accessAttemptsRemaining = ref(null)
const isClinicTheme = computed(() => {
  const theme = orderData.value?.client?.customize?.tracking_style_theme
  return theme === 'nyu' || theme === 'cedra'
})

const store = useStore()
const route = useRoute()
const { locale, t } = useI18n()
const trackingLocales = ['es', 'ko', 'ru', 'vi', 'zh', 'pt-PT', 'pt-BR']

const applyRecipientLocale = lang => {
  if (lang && trackingLocales.includes(lang)) {
    locale.value = lang
  }
}

const handleError = err => {
  console.error('[App.vue] handleError:', err)

  if (err.response) {
    const { data, status } = err.response
    const apiError = data?.error

    if (apiError === 'access_expired') {
      accessView.value = 'denied-expired'
      return
    }

    if (status === 403) {
      accessView.value = 'denied-forbidden'
      return
    }

    const statusCode = typeof status === 'number' ? status : null
    let message = t('error-page.server-error')

    if (typeof data === 'string') {
      message = data
    } else if (data?.error) {
      message = data.error
    } else if (data?.message) {
      ({ message } = data)
    }

    error.value = {
      status: statusCode,
      message,
    }
  } else if (err instanceof Error) {
    error.value = {
      status: null,
      message: err.message || t('error-page.cant-request'),
    }
  } else {
    error.value = {
      status: null,
      message: t('error-page.cant-request'),
    }
  }
}

const googleLoader = async () => {
  if (window.google && window.google.map && window.google.map.places) {
    //
  } else {
    try {
      const resp = await fetch(`${dashUrl}/ajax/PullCommon.php?action=getGoogleKey`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      })
      const text = await resp.text()
      let parsed = null
      try {
        parsed = JSON.parse(text)
      } catch (e) {
        console.warn('getGoogleKey returned non-JSON response (skipping maps loader):', text?.slice?.(0, 120) || text)
        return
      }
      const apikey = parsed?.apikey
      if (!apikey) {
        console.warn('getGoogleKey JSON has no apikey (skipping maps loader):', parsed)
        return
      }
      await loadScript(`https://maps.googleapis.com/maps/api/js?key=${apikey}&libraries=places&language=en`)
    } catch (e) {
      console.warn('Failed to fetch Google Maps key (skipping maps loader):', e)
    }
  }
}

const formatOrderKey = orderUid => {
  let k = CryptoJS.MD5(orderUid)
    .toString()
  const lastChar = parseInt(orderUid.slice(-1), 10)
  const secondLastChar = parseInt(orderUid.slice(-2, -1), 10)
  let last = Math.floor((lastChar + secondLastChar) / 2)
  last = Math.min(last, 5)
  k = k.slice(-last)
  return k
}

const parseOrderFromUrl = urlParams => {
  let orderId = urlParams.get('o')
  let timestamp = urlParams.get('t')

  if (!orderId && urlParams.toString()) {
    const firstEntry = urlParams.entries().next().value
    if (firstEntry) {
      orderId = NotationHelper.strToDec(firstEntry[0])
      timestamp = NotationHelper.strToDec(firstEntry[1])
    }
  }

  return { orderId, timestamp }
}

const loadTrackingApp = async (orderId, timestamp, urlParams) => {
  await googleLoader()

  store.commit('setUrlParams', urlParams.toString())
  try {
    const { order, driver_note } = await getOrderInfo(orderId, timestamp)
    store.commit('setOrderData', order)
    store.commit('setDriverNote', driver_note)
    store.commit('setPhones', order?.facility?.uid)

    let routeInfo = null
    if (order.routeid && order.status !== 'Delivered') {
      routeInfo = await getRouteInfo(order.uid)
      store.commit('setRouteData', routeInfo)
    }
    applyRecipientLocale(order?.recipient?.lang)

    const deliveringState = await setDeliveringState(order, routeInfo)
    store.commit('setDeliveringState', deliveringState)

    orderFound.value = true
    orderData.value = order
    accessView.value = 'app'

    // Push prompt (only when enabled globally+facility and user has no subscription)
    try {
      const orderHash = formatOrderKey(String(orderId))
      const openedFromPush = urlParams.get('push') === '1'
      console.log('[push] App.vue: orderId=', orderId, 'orderHash=', orderHash, 'openedFromPush=', openedFromPush)
      await confirmViewedIfOpenedFromPush(String(orderId), orderHash, openedFromPush)
      console.log('[push] App.vue: calling initPushPrompt')
      await initPushPrompt({ orderUid: String(orderId), orderHash, notificationType: 'order_status' })
    } catch (e) {
      console.error('[push] App.vue catch', e)
      // ignore
    }

    await nextTick(() => {
      const pageSections = document.querySelectorAll('.section')
      if (pageSections.length > 0) {
        const lastSection = pageSections[pageSections.length - 1]
        lastSection.style.marginBottom = '0'
      }
    })
  } catch (err) {
    handleError(err)
  }
}

let intervalId
const startOrderUpdateCheck = () => {
  if (intervalId) {
    return
  }
  intervalId = setInterval(async () => {
    try {
      if (!store.state.orderData || !store.state.orderData.uid) {
        return
      }

      const { changed } = await orderUpdated(store.state.orderData.uid, store.state.orderData)
      if (changed) {
        window.location.reload()
      }
    } catch (err) {
      console.error('Error with orderUpdated:', err)
      clearInterval(intervalId)
    }
  }, 10000)
}

const onAccessVerified = async accessToken => {
  saveSession(pendingOrderId.value, accessToken)
  const urlParams = new URLSearchParams(window.location.search)
  await loadTrackingApp(pendingOrderId.value, pendingTimestamp.value, urlParams)
  startOrderUpdateCheck()
}

const resolveAccess = async (orderId, timestamp, urlParams) => {
  const bypassKey = resolveBypassKey(urlParams)
  pendingOrderId.value = orderId
  pendingTimestamp.value = timestamp
  pendingBypassKey.value = bypassKey

  if (bypassKey) {
    setCurrentBypassKey(bypassKey)
  }

  const storedToken = getStoredSession(orderId)
  if (storedToken) {
    setCurrentAccessToken(storedToken)
  }

  try {
    const status = await getAccessStatus(orderId, {
      bypassKey,
      accessToken: storedToken,
    })

    applyRecipientLocale(status.lang)

    if (status.expired) {
      accessView.value = 'denied-expired'
      return
    }

    accessBlocked.value = !!status.blocked
    accessBlockedSeconds.value = status.blocked_remaining_seconds || 0
    accessAttemptsRemaining.value = status.attempts_remaining

    if (status.authenticated || status.bypass_valid) {
      if (status.bypass_valid && !storedToken) {
        setCurrentBypassKey(bypassKey)
      }
      await loadTrackingApp(orderId, timestamp, urlParams)
      startOrderUpdateCheck()
      return
    }

    accessView.value = 'auth'
  } catch (err) {
    handleError(err)
  }
}

onMounted(async () => {
  const urlParams = new URLSearchParams(window.location.search)
  const cValue = urlParams.get('c')

  if (route.path === '/sg') {
    signatureLink.value = true
  }

  if (cValue !== null && parseInt(cValue, 10) > 0) {
    const orderId = urlParams.get('o')
    const { call } = await getCall(orderId)
    store.commit('setCallData', call)
    callFeedback.value = true
    return
  }

  const { orderId, timestamp } = parseOrderFromUrl(urlParams)

  if (!orderId && !timestamp) {
    error.value = {
      status: 404,
      message: t('error-page.no-order-id'),
    }
    return
  }

  if (!trackingAuthEnabled) {
    await loadTrackingApp(orderId, timestamp, urlParams)
    startOrderUpdateCheck()
    return
  }

  await resolveAccess(orderId, timestamp, urlParams)
})
</script>
